import type { Lang } from './languages.js';
import type { LexiconSet } from './lexicon.js';
import type { LanguageRequirement, ModeDef, ModeId } from './modes.js';
import { languageRequirement, MODES, targetWordsFor } from './modes.js';
import type { RuleSet } from './rules.js';
import { getRuleSet, wordPoints } from './rules.js';
import type { ScoreBreakdown } from './scoring.js';
import { computeScore, countByLanguage, isLanguageRequirementFulfilled } from './scoring.js';
import { canFormKey, isValidKey, tileCounts } from './tiles.js';

/**
 * Pure state machine of one player's round. The server is the authority: it calls these
 * functions with its own clock. Clients run the same code for instant feedback and for
 * offline/solo games (which never enter online leaderboards).
 */
export type EndReason = 'target_reached' | 'time_up' | 'stopped' | 'abandoned';

export interface RoundConfig {
  mode: ModeId;
  rulesVersion: string;
  rack: string[];
  /** Languages allowed for this player, in the player's order. */
  languages: Lang[];
}

export interface AcceptedWord {
  seq: number;
  key: string;
  display: string;
  lang: Lang;
  tiles: number;
  points: number;
  /** Elapsed time (server clock) at acceptance, capped at the time limit. */
  atMs: number;
}

export interface RoundState {
  config: RoundConfig;
  /** Absolute start time (ms, authoritative clock). */
  startedAt: number;
  words: AcceptedWord[];
  status: 'playing' | 'finished';
  endReason: EndReason | null;
  /** Elapsed ms at the end of the round. */
  endedAtMs: number | null;
  /** Elapsed ms when the Duel target was reached. */
  targetReachedAtMs: number | null;
}

export type RejectReason =
  | 'round_over'
  | 'invalid_characters'
  | 'too_short'
  | 'too_long'
  | 'not_formable'
  | 'duplicate'
  | 'language_not_allowed'
  | 'excluded_word'
  | 'not_a_word'
  | 'wrong_language'
  | 'confirmation_required'
  // Server-side only (never produced by the engine itself):
  | 'not_started'
  | 'rate_limited';

export interface SubmitInput {
  key: string;
  /** Attribution chosen by the player; when absent, the deterministic proposal is used. */
  lang?: Lang | null;
  /** Explicit confirmation required for a final (target) word that leaves the contract unmet. */
  confirmFinal?: boolean;
}

export type SubmitOutcome =
  | { ok: true; word: AcceptedWord; finished: boolean }
  | { ok: false; reason: RejectReason; validLangs?: Lang[] };

export interface Candidate {
  key: string;
  validLangs: Lang[];
  proposal: Lang | null;
  displays: Partial<Record<Lang, string>>;
  reason: RejectReason | null;
}

export function modeOf(state: RoundState): ModeDef {
  return MODES[state.config.mode];
}

export function rulesOf(state: RoundState): RuleSet {
  return getRuleSet(state.config.rulesVersion);
}

export function requirementOf(state: RoundState): LanguageRequirement {
  return languageRequirement(modeOf(state), state.config.languages, rulesOf(state));
}

export function targetOf(state: RoundState): number | null {
  return targetWordsFor(modeOf(state), rulesOf(state));
}

export function timeLimitOf(state: RoundState): number | null {
  return modeOf(state).timed ? rulesOf(state).timeLimitMs : null;
}

export function createRound(config: RoundConfig, startedAt: number): RoundState {
  const rules = getRuleSet(config.rulesVersion);
  if (config.rack.length !== rules.rackSize) throw new Error('Invalid rack size');
  if (config.languages.length === 0) throw new Error('No language');
  return {
    config: { ...config, rack: [...config.rack], languages: [...config.languages] },
    startedAt,
    words: [],
    status: 'playing',
    endReason: null,
    endedAtMs: null,
    targetReachedAtMs: null,
  };
}

/** Deterministic attribution proposal: the valid language that most needs words for the quota. */
export function proposeLanguage(
  validLangs: readonly Lang[],
  state: RoundState,
  requirement: LanguageRequirement = requirementOf(state),
): Lang | null {
  if (validLangs.length === 0) return null;
  const order = state.config.languages;
  const counts = countByLanguage(state.words, order);
  const countOf = (l: Lang): number => counts.find((c) => c.lang === l)?.words ?? 0;
  const sorted = [...validLangs].sort((a, b) => order.indexOf(a) - order.indexOf(b));
  if (requirement.quotaPerLanguage > 0) {
    const needing = sorted.filter((l) => countOf(l) < requirement.quotaPerLanguage);
    if (needing.length > 0) {
      return needing.reduce((best, l) => (countOf(l) < countOf(best) ? l : best), needing[0]!);
    }
  }
  return sorted[0]!;
}

/** Evaluates a key without changing the state (client feedback and server validation). */
export function evaluateCandidate(
  state: RoundState,
  rawKey: string,
  lexicons: LexiconSet,
): Candidate {
  const rules = rulesOf(state);
  const key = rawKey.normalize('NFC').toUpperCase();
  const base: Candidate = { key, validLangs: [], proposal: null, displays: {}, reason: null };
  if (!isValidKey(key)) return { ...base, reason: 'invalid_characters' };
  if (key.length < rules.minWordLength) return { ...base, reason: 'too_short' };
  if (key.length > rules.maxWordLength) return { ...base, reason: 'too_long' };
  if (!canFormKey(key, tileCounts(state.config.rack))) return { ...base, reason: 'not_formable' };
  if (state.words.some((w) => w.key === key)) return { ...base, reason: 'duplicate' };
  const validLangs: Lang[] = [];
  const displays: Partial<Record<Lang, string>> = {};
  let excluded = false;
  for (const lang of state.config.languages) {
    const lex = lexicons[lang];
    if (!lex) continue;
    const entry = lex.lookup(key);
    if (entry) {
      validLangs.push(lang);
      displays[lang] = entry.displays[0]!;
    } else if (lex.isExcluded(key)) {
      excluded = true;
    }
  }
  if (validLangs.length === 0)
    return { ...base, reason: excluded ? 'excluded_word' : 'not_a_word' };
  return { key, validLangs, displays, proposal: proposeLanguage(validLangs, state), reason: null };
}

function finish(state: RoundState, reason: EndReason, endedAtMs: number): RoundState {
  if (state.status === 'finished') return state;
  return { ...state, status: 'finished', endReason: reason, endedAtMs };
}

/** Elapsed ms since the start according to the authoritative clock. */
export function elapsedMs(state: RoundState, now: number): number {
  return Math.max(0, now - state.startedAt);
}

/** Closes the round if its time (plus the uniform latency grace) is over. Idempotent. */
export function finalizeIfExpired(state: RoundState, now: number): RoundState {
  const limit = timeLimitOf(state);
  if (state.status === 'finished' || limit === null) return state;
  if (elapsedMs(state, now) > limit + rulesOf(state).latencyGraceMs)
    return finish(state, 'time_up', limit);
  return state;
}

/** Voluntary stop (no bonus is ever granted for stopping early). Idempotent. */
export function stopRound(
  state: RoundState,
  now: number,
  reason: 'stopped' | 'abandoned' = 'stopped',
): RoundState {
  const expired = finalizeIfExpired(state, now);
  if (expired.status === 'finished') return expired;
  const limit = timeLimitOf(state);
  const e = elapsedMs(state, now);
  return finish(state, reason, limit === null ? e : Math.min(e, limit));
}

export function submitWord(
  state: RoundState,
  input: SubmitInput,
  now: number,
  lexicons: LexiconSet,
): { state: RoundState; outcome: SubmitOutcome } {
  const current = finalizeIfExpired(state, now);
  if (current.status === 'finished')
    return { state: current, outcome: { ok: false, reason: 'round_over' } };

  const cand = evaluateCandidate(current, input.key, lexicons);
  if (cand.reason !== null) return { state: current, outcome: { ok: false, reason: cand.reason } };

  let lang: Lang;
  if (input.lang != null) {
    if (!current.config.languages.includes(input.lang)) {
      return {
        state: current,
        outcome: { ok: false, reason: 'language_not_allowed', validLangs: cand.validLangs },
      };
    }
    if (!cand.validLangs.includes(input.lang)) {
      return {
        state: current,
        outcome: { ok: false, reason: 'wrong_language', validLangs: cand.validLangs },
      };
    }
    lang = input.lang;
  } else {
    lang = cand.proposal!;
  }

  const rules = rulesOf(current);
  const limit = timeLimitOf(current);
  const e = elapsedMs(current, now);
  const atMs = limit === null ? e : Math.min(e, limit);
  const word: AcceptedWord = {
    seq: current.words.length + 1,
    key: cand.key,
    display: cand.displays[lang]!,
    lang,
    tiles: cand.key.length,
    points: wordPoints(cand.key.length, rules),
    atMs,
  };

  const target = targetOf(current);
  const words = [...current.words, word];
  const isFinal = target !== null && words.length >= target;
  if (isFinal && !input.confirmFinal) {
    const requirement = requirementOf(current);
    const fulfilledAfter =
      requirement.quotaPerLanguage === 0 ||
      isLanguageRequirementFulfilled(requirement, countByLanguage(words, current.config.languages));
    if (!fulfilledAfter) {
      return {
        state: current,
        outcome: { ok: false, reason: 'confirmation_required', validLangs: cand.validLangs },
      };
    }
  }

  let next: RoundState = { ...current, words };
  if (isFinal) {
    next = { ...finish(next, 'target_reached', atMs), targetReachedAtMs: atMs };
  }
  return { state: next, outcome: { ok: true, word, finished: next.status === 'finished' } };
}

/** Would the next accepted word end the round while leaving the contract unmet? */
export function finalWordNeedsConfirmation(state: RoundState, lang: Lang): boolean {
  const target = targetOf(state);
  if (target === null || state.words.length + 1 < target) return false;
  const requirement = requirementOf(state);
  if (requirement.quotaPerLanguage === 0) return false;
  const words = [...state.words, { lang, points: 0, key: '' }];
  return !isLanguageRequirementFulfilled(
    requirement,
    countByLanguage(words, state.config.languages),
  );
}

export function scoreRound(state: RoundState): ScoreBreakdown {
  const rules = rulesOf(state);
  const mode = modeOf(state);
  return computeScore({
    words: state.words,
    languages: state.config.languages,
    requirement: requirementOf(state),
    speedBonus: mode.speedBonus,
    targetWords: targetOf(state),
    targetReachedAtMs: state.targetReachedAtMs,
    timeLimitMs: rules.timeLimitMs,
    rules,
  });
}
